import { useCallback, useEffect, useRef, useState } from 'react'
import {
  useAtom,
  useAtomValue,
  useSetAtom,
} from 'infrastructure/state/jotai.wrapper'
import {
  canStartAtom,
  includeAudioVideoAtom,
  includeDocumentsAtom,
  includeImagesAtom,
  pipelineErrorAtom,
  pipelineStatusAtom,
  pollingTriggerAtom,
  uploadResultAtom,
  uploadStatusAtom,
} from './atoms'
import {
  deleteAssetUpload,
  getAssetUploadResults,
  getAssetUploadStatus,
  startAssetUpload,
} from './utils'

type ErrorWithStatus = Error & { status?: number }

function getErrorStatus(error: unknown): number | null {
  if (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    typeof (error as ErrorWithStatus).status === 'number'
  ) {
    return (error as ErrorWithStatus).status as number
  }

  return null
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Unknown error'
}

function buildProgressMessage(status: {
  completed: boolean
  dependant_jobs: { completed: number; failed: number; total: number }
  worker_jobs: { completed: number; failed: number; total: number }
}): string {
  const completedJobs =
    status.dependant_jobs.completed +
    status.worker_jobs.completed +
    status.dependant_jobs.failed +
    status.worker_jobs.failed
  const totalJobs = status.dependant_jobs.total + status.worker_jobs.total

  if (status.completed) {
    return 'Upload stage completed.'
  }

  return `Upload stage in progress (${completedJobs}/${totalJobs} jobs completed).`
}

/**
 * Returns a polling callback that continuously refreshes asset upload status.
 *
 * @returns Callback that starts polling and returns a cleanup function.
 */
export function useAssetUploadPolling() {
  const setUploadStatus = useSetAtom(uploadStatusAtom)
  const setPipelineError = useSetAtom(pipelineErrorAtom)
  const setPipelineStatus = useSetAtom(pipelineStatusAtom)
  const pipelineError = useAtomValue(pipelineErrorAtom)
  const pipelineErrorRef = useRef(pipelineError)

  useEffect(() => {
    pipelineErrorRef.current = pipelineError
  }, [pipelineError])

  return useCallback(
    (treeId: string, versionId: string) => {
      let intervalId: NodeJS.Timeout | null = null
      let cancelled = false

      const stopPolling = () => {
        if (intervalId) {
          clearInterval(intervalId)
          intervalId = null
        }
      }

      const pollStatus = async () => {
        if (cancelled || pipelineErrorRef.current) {
          stopPolling()
          return
        }

        try {
          const statusResponse = await getAssetUploadStatus(treeId, versionId)

          if (cancelled) return

          if (statusResponse.errors && statusResponse.errors.length > 0) {
            setPipelineError(statusResponse.errors.join(', '))
            stopPolling()
            return
          }

          if (!statusResponse.data) {
            return
          }

          setUploadStatus(statusResponse.data)
          setPipelineError(null)
          setPipelineStatus(buildProgressMessage(statusResponse.data))

          if (statusResponse.data.completed) {
            stopPolling()
          }
        } catch (error) {
          if (cancelled) return

          if (getErrorStatus(error) === 404) {
            return
          }

          setPipelineError(
            `Error getting upload status: ${getErrorMessage(error)}`,
          )
          stopPolling()
        }
      }

      void pollStatus()
      intervalId = setInterval(() => {
        void pollStatus()
      }, 3000)

      return () => {
        cancelled = true
        stopPolling()
      }
    },
    [setPipelineError, setPipelineStatus, setUploadStatus],
  )
}

/**
 * Manages upload stage configuration, start action, and results loading.
 *
 * @param treeId - Tree ID.
 * @param versionId - Tree version ID.
 * @returns Upload stage state and handlers.
 */
export function useUploadStage(treeId: string, versionId: string) {
  const uploadStatus = useAtomValue(uploadStatusAtom)
  const uploadResult = useAtomValue(uploadResultAtom)
  const pipelineError = useAtomValue(pipelineErrorAtom)
  const [includeImages, setIncludeImages] = useAtom(includeImagesAtom)
  const [includeDocuments, setIncludeDocuments] = useAtom(includeDocumentsAtom)
  const [includeAudioVideo, setIncludeAudioVideo] = useAtom(
    includeAudioVideoAtom,
  )
  const setPipelineError = useSetAtom(pipelineErrorAtom)
  const setUploadResult = useSetAtom(uploadResultAtom)
  const setPipelineStatus = useSetAtom(pipelineStatusAtom)
  const canStartBase = useAtomValue(canStartAtom)
  const [isStarting, setIsStarting] = useState(false)

  const hasSelectedAssetType =
    includeImages || includeDocuments || includeAudioVideo
  const isRunning = uploadStatus !== null && !uploadStatus.completed

  const handleStartStage = useCallback(async () => {
    if (isStarting) return

    if (!hasSelectedAssetType) {
      setPipelineError('Select at least one asset type before starting upload.')
      return
    }

    setIsStarting(true)
    setPipelineError(null)

    try {
      setPipelineStatus('Upload stage started...')
      await startAssetUpload(
        treeId,
        versionId,
        includeImages,
        includeDocuments,
        includeAudioVideo,
      )
    } catch (error) {
      if (getErrorStatus(error) === 409) {
        setPipelineError(null)
        setPipelineStatus(
          'Upload already in progress. Tracking existing pipeline.',
        )
      } else {
        setPipelineError(
          `Error starting upload stage: ${getErrorMessage(error)}`,
        )
      }
    } finally {
      setIsStarting(false)
    }
  }, [
    includeAudioVideo,
    includeDocuments,
    includeImages,
    hasSelectedAssetType,
    isStarting,
    setPipelineError,
    setPipelineStatus,
    treeId,
    versionId,
  ])

  useEffect(() => {
    let cancelled = false
    let retryTimeoutId: NodeJS.Timeout | null = null

    const fetchResults = async () => {
      if (
        cancelled ||
        !uploadStatus?.completed ||
        uploadResult ||
        pipelineError
      ) {
        return
      }

      try {
        const resultsResponse = await getAssetUploadResults(treeId, versionId)

        if (cancelled) return

        if (resultsResponse.errors && resultsResponse.errors.length > 0) {
          setPipelineError(resultsResponse.errors.join(', '))
          return
        }

        if (resultsResponse.data) {
          setUploadResult(resultsResponse.data)
        }
      } catch (error) {
        if (cancelled) return

        if (getErrorStatus(error) === 409) {
          setPipelineStatus('Upload completed. Waiting for results...')
          retryTimeoutId = setTimeout(() => {
            void fetchResults()
          }, 3000)
          return
        }

        setPipelineError(
          `Error fetching upload results: ${getErrorMessage(error)}`,
        )
      }
    }

    void fetchResults()

    return () => {
      cancelled = true
      if (retryTimeoutId) {
        clearTimeout(retryTimeoutId)
      }
    }
  }, [
    pipelineError,
    setPipelineError,
    setPipelineStatus,
    setUploadResult,
    treeId,
    uploadResult,
    uploadStatus,
    versionId,
  ])

  return {
    uploadStatus,
    uploadResult,
    includeImages,
    includeDocuments,
    includeAudioVideo,
    isRunning,
    setIncludeImages,
    setIncludeDocuments,
    setIncludeAudioVideo,
    handleStartStage,
    canStart: canStartBase && !isStarting && hasSelectedAssetType,
  }
}

/**
 * Manages top-level pipeline status and reset/delete behavior.
 *
 * @param treeId - Tree ID.
 * @param versionId - Tree version ID.
 * @returns Pipeline status state and reset handler.
 */
export function usePipelineStatus(treeId: string, versionId: string) {
  const [pipelineStatus, setPipelineStatus] = useAtom(pipelineStatusAtom)
  const [error, setError] = useAtom(pipelineErrorAtom)
  const setUploadStatus = useSetAtom(uploadStatusAtom)
  const setUploadResult = useSetAtom(uploadResultAtom)
  const setIncludeImages = useSetAtom(includeImagesAtom)
  const setIncludeDocuments = useSetAtom(includeDocumentsAtom)
  const setIncludeAudioVideo = useSetAtom(includeAudioVideoAtom)
  const checkStatus = useAssetUploadPolling()
  const [pollingTrigger, setPollingTrigger] = useAtom(pollingTriggerAtom)

  const resetState = useCallback(() => {
    setUploadStatus(null)
    setUploadResult(null)
    setIncludeImages(true)
    setIncludeDocuments(true)
    setIncludeAudioVideo(true)
    setError(null)
  }, [
    setError,
    setIncludeAudioVideo,
    setIncludeDocuments,
    setIncludeImages,
    setUploadResult,
    setUploadStatus,
  ])

  useEffect(() => {
    const cleanup = checkStatus(treeId, versionId)
    return cleanup
  }, [checkStatus, treeId, versionId, pollingTrigger])

  const handleRemovePipeline = useCallback(async () => {
    try {
      await deleteAssetUpload(treeId, versionId)
      resetState()
      setPipelineStatus('Pipeline reset successfully.')
      setPollingTrigger((prev) => prev + 1)
    } catch (error) {
      if (getErrorStatus(error) === 404) {
        resetState()
        setPipelineStatus('No existing pipeline found. Local state reset.')
        setPollingTrigger((prev) => prev + 1)
        return
      }

      setPipelineStatus(`Error resetting pipeline: ${getErrorMessage(error)}`)
    }
  }, [resetState, setPollingTrigger, setPipelineStatus, treeId, versionId])

  return {
    pipelineStatus,
    error,
    handleRemovePipeline,
  }
}
